import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuEye } from 'react-icons/lu';
import { useReportData } from './lib/report-context';
import GeneralViewerInstanceDetail from '../GeneralViewer/GeneralViewerInstanceDetail';
import type { GeneralViewerInstance } from '../../../helpers/api/view_frame/view-frames';
import scansImage from '../../assets/scans_00000.png';
import type {
  FindingAdditionalInfoBlock,
  FindingFrameRange,
} from '../../../helpers/findingsProvenance';

type TFn = (key: string, options?: Record<string, unknown>) => string;

const FINDING_TITLE_DEFAULTS: Record<string, string> = {
  presentation: 'Fetal Presentation',
  pregnancy_type: 'Pregnancy Type',
  placenta_localization: 'Placenta Localization',
  placenta_distance_to_cervix: 'Placenta – Cervix Distance',
  cardiac_activity: 'Cardiac Activity',
  sex_prediction: 'Fetal Sex',
  pre_eclampsia: 'Pre-eclampsia Screening',
  gestational_diabetes: 'Gestational Diabetes Screening',
};

const FIELD_LABEL_DEFAULTS: Record<string, string> = {
  value: 'Result',
  confidence: 'Confidence',
  facing: 'Facing',
  decision_case: 'Decision',
  decision_tier: 'Decision tier',
  primary_sweep: 'Primary sweep',
  deciding_sweep: 'Deciding sweep',
  contributing_sweeps: 'Contributing sweeps',
  head_count_sweep5: 'Head frames · sweep 5',
  head_count_sweep7: 'Head frames · sweep 7',
  balance_ratio: 'Balance ratio',
  lie_angle_deg: 'Lie angle (°)',
  trimester: 'Trimester',
  twin_candidate_frames: 'Twin candidate frames',
  total_evaluated_frames: 'Frames evaluated',
  above_frames: 'Votes above',
  below_frames: 'Votes below',
  same_frames: 'Votes same',
  distance_cm: 'Distance (cm)',
  min_distance_px: 'Min distance (px)',
  pixel_spacing_cm: 'Pixel spacing (cm)',
  present: 'Detected',
  heart_rate_bpm: 'Heart rate (bpm)',
  heart_class_index: 'Heart class index',
  seen: 'Genitalia seen',
  risk_level: 'Risk level',
};

const SKIP_SUMMARY_KEYS = new Set(['source', 'frame_range', 'evidence', 'value']);

function titleFor(t: TFn, findingKey: string, labelKey?: string): string {
  return t(`explain.title.${findingKey}`, {
    defaultValue:
      FINDING_TITLE_DEFAULTS[findingKey] ?? labelKey ?? findingKey.replace(/_/g, ' '),
  });
}

function labelFor(t: TFn, key: string): string {
  return t(`explain.field.${key}`, {
    defaultValue: FIELD_LABEL_DEFAULTS[key] ?? key.replace(/_/g, ' '),
  });
}

function formatScalar(t: TFn, value: unknown): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'boolean') {
    return value ? t('explain.yes', { defaultValue: 'Yes' }) : t('explain.no', { defaultValue: 'No' });
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : null;
  }
  if (typeof value === 'object') {
    return null; // rendered via evidence section, not as a scalar row
  }
  return String(value);
}


function SweepGuideAnimation({ sweep }: { sweep?: number | null }) {
  const animationClass = `ultrasound-sweep-${sweep ?? 1}`;
  const pathD =
    (
      {
        'ultrasound-sweep-1': 'M 50 92 V 70 M 85 70 H 15',
        'ultrasound-sweep-2': 'M 25 45 V 92',
        'ultrasound-sweep-3': 'M 50 45 V 92',
        'ultrasound-sweep-4': 'M 75 45 V 92',
        'ultrasound-sweep-5': 'M 15 50 H 85',
        'ultrasound-sweep-6': 'M 15 75 H 85',
        'ultrasound-sweep-7': 'M 20 80 Q 50 100 80 80',
      } as Record<string, string>
    )[animationClass] || '';

  return (
    <div className="relative w-full aspect-[4/3] overflow-hidden rounded-md bg-slate-200 dark:bg-gray-700 pointer-events-none">
      <div
        className="absolute inset-0 w-full h-full opacity-50"
        style={{
          backgroundImage: `url(${scansImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <svg
        className="absolute inset-0 w-full h-full z-10"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <path
          d={pathD}
          fill="none"
          stroke="rgba(59, 130, 246, 0.6)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="4 4"
          className="fe-path-anim"
        />
      </svg>

      <div className={`absolute z-20 fe-${animationClass}`}>
        <div style={{ transform: 'translate(-50%, -50%)' }}>
          <div
            style={{
              width: 34,
              height: 18,
              borderRadius: 8,
              background: '#e5e7eb',
              border: '1.2px solid rgba(148,163,184,0.9)',
              boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: '20%',
                bottom: '20%',
                width: 3,
                background: '#3b82f6',
                borderRadius: '0 3px 3px 0',
                boxShadow: '0 0 10px rgba(59,130,246,0.8)',
              }}
            />
            <div
              style={{
                width: 18,
                height: 9,
                borderRadius: 4,
                border: '1.2px solid rgba(203,213,225,0.9)',
                background: 'rgba(243,244,246,0.95)',
              }}
            />
          </div>
        </div>
      </div>

      {/* Scoped keyframes (prefixed `fe-` to avoid clashing with the realtime page's rules). */}
      <style>{`
.fe-path-anim { animation: fe-draw-path 3s ease-in-out infinite alternate; }
@keyframes fe-draw-path { from { stroke-dashoffset: 100; } to { stroke-dashoffset: 0; } }
@keyframes fe-sweep-1 {
  0%   { left: 50%; top: 92%; transform: rotate(0deg); opacity: 1; }
  30%  { left: 50%; top: 70%; transform: rotate(0deg); opacity: 1; }
  35%  { left: 82%; top: 70%; transform: rotate(90deg); opacity: 0; }
  40%  { left: 82%; top: 70%; transform: rotate(90deg); opacity: 1; }
  100% { left: 18%; top: 70%; transform: rotate(90deg); opacity: 1; }
}
.fe-ultrasound-sweep-1 { animation: fe-sweep-1 4s ease-in-out infinite alternate; }
@keyframes fe-sweep-v { 0% { top: 45%; opacity: 0.8; transform: rotate(0deg); } 100% { top: 92%; opacity: 1; transform: rotate(0deg); } }
.fe-ultrasound-sweep-2 { left: 25%; animation: fe-sweep-v 2s ease-in-out infinite alternate; }
.fe-ultrasound-sweep-3 { left: 50%; animation: fe-sweep-v 2s ease-in-out infinite alternate 0.3s; }
.fe-ultrasound-sweep-4 { left: 75%; animation: fe-sweep-v 2s ease-in-out infinite alternate 0.6s; }
@keyframes fe-sweep-h { 0% { left: 15%; opacity: 0.8; transform: rotate(90deg); } 100% { left: 85%; opacity: 1; transform: rotate(90deg); } }
.fe-ultrasound-sweep-5 { top: 50%; animation: fe-sweep-h 2.5s ease-in-out infinite alternate; }
.fe-ultrasound-sweep-6 { top: 75%; animation: fe-sweep-h 2.5s ease-in-out infinite alternate 0.5s; }
@keyframes fe-sweep-c { 0% { left: 20%; top: 80%; transform: rotate(90deg); } 50% { left: 50%; top: 90%; transform: rotate(90deg); } 100% { left: 80%; top: 80%; transform: rotate(90deg); } }
.fe-ultrasound-sweep-7 { animation: fe-sweep-c 3s ease-in-out infinite alternate; }
`}</style>
    </div>
  );
}

function SweepMotionPanel({ sweep }: { sweep?: number | null }) {
  const { t } = useTranslation('Report');
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
        {t('explain.sweepMotion', { defaultValue: 'Sweep motion' })}
      </p>
      <SweepGuideAnimation sweep={sweep} />
      <p className="mt-1.5 text-[10px] text-slate-400 dark:text-gray-500">
        {t('explain.sweepMotionHint', {
          defaultValue: 'Probe path for sweep {{n}}',
          n: sweep ?? '—',
        })}
      </p>
    </div>
  );
}

function SourceBadge({ source }: { source?: string | null }) {
  const { t } = useTranslation('Report');
  if (source === 'ai') {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
        {t('explain.source.ai', { defaultValue: 'AI' })}
      </span>
    );
  }
  if (source === 'manual') {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
        {t('explain.source.manual', { defaultValue: 'Manual' })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-gray-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400">
      {t('explain.source.unknown', { defaultValue: 'Unknown' })}
    </span>
  );
}

function FrameRangeList({ frameRange }: { frameRange?: FindingFrameRange[] }) {
  const { t } = useTranslation('Report');
  if (!frameRange || frameRange.length === 0) {
    return (
      <p className="text-xs text-slate-400 dark:text-gray-500 italic">
        {t('explain.noFrameEvidence', {
          defaultValue: 'No frame evidence recorded for this finding.',
        })}
      </p>
    );
  }
  return (
    <ul className="space-y-1.5">
      {frameRange.map((entry, idx) => {
        const frames = entry.frames ?? entry.frame_indices ?? [];
        return (
          <li
            key={`${entry.sweep ?? 'na'}-${idx}`}
            className="flex items-start gap-2 text-xs"
          >
            <span className="shrink-0 rounded bg-slate-100 dark:bg-gray-800 px-1.5 py-0.5 font-semibold text-slate-600 dark:text-gray-300">
              {t('explain.sweepShort', { defaultValue: 'Sweep {{n}}', n: entry.sweep ?? '—' })}
            </span>
            <span className="text-slate-600 dark:text-gray-300 break-words">
              {frames.length
                ? t('explain.framesLabel', {
                    defaultValue: 'frames {{list}}',
                    list: frames.join(', '),
                  })
                : t('explain.noFrames', { defaultValue: 'no frames' })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** A sweep entry is playable once the backend has resolved its DB references. */
function isPlayable(entry: FindingFrameRange): boolean {
  return Boolean(entry.sop_instance_uid && entry.serie_instance_uid);
}

function buildInstance(entry: FindingFrameRange): GeneralViewerInstance {
  return {
    sop_instance_uid: String(entry.sop_instance_uid),
    serie_instance_uid: String(entry.serie_instance_uid),
    frame_count: typeof entry.frame_count === 'number' ? entry.frame_count : 0,
    first_frame_image_url: null,
    format_file: entry.format_file === 'VIDEO' ? 'VIDEO' : 'DICOM',
    fps: entry.fps ?? null,
    width: entry.width ?? null,
    height: entry.height ?? null,
    modality: entry.modality ?? null,
    sop_class_uid: entry.sop_class_uid ?? null,
  };
}

function InfoPanel({
  block,
  compact,
  sweep,
}: {
  block: FindingAdditionalInfoBlock;
  compact?: boolean;
  /** Selected sweep number; drives the sweep-motion illustration (replaces raw evidence). */
  sweep?: number | null;
}) {
  const { t } = useTranslation('Report');

  const summaryRows = useMemo(() => {
    const rows: Array<{ key: string; label: string; value: string }> = [];
    Object.entries(block).forEach(([key, raw]) => {
      if (SKIP_SUMMARY_KEYS.has(key)) {
        return;
      }
      const formatted = formatScalar(t, raw);
      if (formatted !== null) {
        rows.push({ key, label: labelFor(t, key), value: formatted });
      }
    });
    return rows;
  }, [block, t]);

  const resolvedValue = formatScalar(t, block.value);

  return (
    <div className="space-y-4">
      {resolvedValue !== null && (
        <div className="rounded-lg bg-slate-50 dark:bg-gray-900 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-gray-500">
            {t('explain.result', { defaultValue: 'Result' })}
          </p>
          <p className="text-base font-semibold text-slate-800 dark:text-gray-100 capitalize">
            {resolvedValue}
          </p>
        </div>
      )}

      {summaryRows.length > 0 && (
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
            {t('explain.decisionSummary', { defaultValue: 'Decision summary' })}
          </p>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {summaryRows.map((row) => (
              <div key={row.key} className="min-w-0">
                <dt className="text-[11px] text-slate-400 dark:text-gray-500">
                  {row.label}
                </dt>
                <dd className="text-xs font-medium text-slate-700 dark:text-gray-200 break-words">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {compact && (
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
            {t('explain.frameEvidence', { defaultValue: 'Frame evidence' })}
          </p>
          <FrameRangeList frameRange={block.frame_range} />
        </div>
      )}

      {/* Sweep-motion illustration replaces the former "Raw evidence" block. */}
      <SweepMotionPanel sweep={sweep} />
    </div>
  );
}

interface FindingExplainabilityModalProps {
  findingKey: string;
  block: FindingAdditionalInfoBlock;
  onClose: () => void;
  studyUID?: string;
}

export function FindingExplainabilityModal({
  findingKey,
  block,
  onClose,
  studyUID: studyUIDProp,
}: FindingExplainabilityModalProps) {
  const { t } = useTranslation('Report');
  const ctx = useReportData();
  const studyUID = studyUIDProp ?? ctx.studyUID;
  const [sweepIdx, setSweepIdx] = useState(0);
  const [frameIdx, setFrameIdx] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title = titleFor(t, findingKey);

  const playable = useMemo(
    () => (block.frame_range ?? []).filter(isPlayable),
    [block.frame_range]
  );

  const canPlay = playable.length > 0 && Boolean(studyUID);

  const activeSweep = playable[Math.min(sweepIdx, playable.length - 1)];
  const activeInstance = useMemo(
    () => (activeSweep ? buildInstance(activeSweep) : null),
    [activeSweep]
  );
  const provingFrames = activeSweep?.frames ?? activeSweep?.frame_indices ?? [];
  const initialFrameId =
    activeSweep?.frame_ids?.[Math.min(frameIdx, (activeSweep.frame_ids?.length ?? 1) - 1)];

  // In the text-fallback mode there is no active player sweep; illustrate the first recorded one.
  const fallbackSweep = block.frame_range?.[0]?.sweep ?? null;

  if (!canPlay || !activeInstance) {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={onClose}
      >
        <div
          className="w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden rounded-2xl bg-white dark:bg-gray-950 shadow-2xl border border-slate-200 dark:border-gray-800"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-gray-800">
            <div className="min-w-0 flex items-center gap-2">
              <p className="truncate text-sm font-semibold text-slate-800 dark:text-gray-100">
                {title}
              </p>
              <SourceBadge source={block.source} />
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-500 dark:text-gray-400 text-xl transition-colors"
              aria-label={t('explain.close', { defaultValue: 'Close' })}
            >
              ×
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <InfoPanel block={block} compact sweep={fallbackSweep} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/80 backdrop-blur-sm p-3 sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-2xl bg-white dark:bg-gray-950 shadow-2xl border border-white/10">
        <div className="h-12 shrink-0 flex items-center justify-between gap-3 px-4 border-b border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="min-w-0 flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-slate-800 dark:text-gray-100">
              {title}
            </p>
            <SourceBadge source={block.source} />
            <span className="text-[10px] text-slate-400 dark:text-gray-500">
              {t('explain.playHint', {
                defaultValue: 'Play the sweep to review the proving frames',
              })}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-500 dark:text-gray-400 text-xl transition-colors"
            aria-label={t('explain.close', { defaultValue: 'Close' })}
          >
            ×
          </button>
        </div>

        <div className="flex-1 min-h-0 flex">
          <aside className="w-64 shrink-0 flex flex-col border-r border-slate-200 dark:border-gray-800 overflow-y-auto">
            <div className="p-3 border-b border-slate-200 dark:border-gray-800">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
                {t('explain.sweeps', { defaultValue: 'Sweeps' })} ({playable.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {playable.map((entry, idx) => {
                  const count =
                    (entry.frames ?? entry.frame_indices ?? []).length;
                  const active = idx === sweepIdx;
                  return (
                    <button
                      key={`${entry.sweep ?? idx}`}
                      type="button"
                      onClick={() => {
                        setSweepIdx(idx);
                        setFrameIdx(0);
                      }}
                      className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                        active
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-300 hover:bg-slate-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t('explain.sweepShort', {
                        defaultValue: 'Sweep {{n}}',
                        n: entry.sweep ?? idx + 1,
                      })}
                      <span className="ml-1 opacity-70">· {count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {provingFrames.length > 0 && (
              <div className="p-3 border-b border-slate-200 dark:border-gray-800">
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
                  {t('explain.provingFrames', { defaultValue: 'Proving frames' })}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {provingFrames.map((fn, idx) => {
                    const active = idx === frameIdx;
                    const hasRef = Boolean(activeSweep?.frame_ids?.[idx]);
                    const displayFn = (typeof fn === 'number' ? fn : Number(fn)) + 1;
                    return (
                      <button
                        key={`${fn}-${idx}`}
                        type="button"
                        disabled={!hasRef}
                        onClick={() => setFrameIdx(idx)}
                        title={
                          hasRef
                            ? t('explain.jumpToFrame', {
                                defaultValue: 'Jump to frame {{n}}',
                                n: displayFn,
                              })
                            : t('explain.frameNotResolved', {
                                defaultValue: 'Frame {{n}} (reference not resolved)',
                                n: displayFn,
                              })
                        }
                        className={`rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors ${
                          active
                            ? 'bg-blue-600 text-white'
                            : hasRef
                              ? 'bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-300 hover:bg-slate-200 dark:hover:bg-gray-700'
                              : 'bg-slate-50 dark:bg-gray-900 text-slate-300 dark:text-gray-600 cursor-not-allowed'
                        }`}
                      >
                        {String(displayFn)}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-[10px] text-slate-400 dark:text-gray-500">
                  {t('explain.provingFramesHint', {
                    defaultValue:
                      'Opens at the selected frame; press play to run the whole sweep.',
                  })}
                </p>
              </div>
            )}

            <div className="p-3">
              <InfoPanel block={block} sweep={activeSweep?.sweep ?? null} />
            </div>
          </aside>

          <div className="flex-1 min-h-0">
            <GeneralViewerInstanceDetail
              key={activeInstance.sop_instance_uid}
              studyUID={studyUID as string}
              instance={activeInstance}
              onBack={onClose}
              initialFrameId={initialFrameId}
              readOnly
              lazyImageFetch
              priorityFrameIds={activeSweep?.frame_ids}
            />
          </div>
        </div>
      </div>
    </div>
  );
}


export function FindingExplainEye({
  findingKey,
  labelKey,
  block: blockProp,
  studyUID: studyUIDProp,
  size = 12,
}: {
  findingKey: string;
  labelKey?: string;
  block?: FindingAdditionalInfoBlock;
  studyUID?: string;
  size?: number;
}) {
  const { t } = useTranslation('Report');
  const ctx = useReportData();
  const [open, setOpen] = useState(false);

  const block = blockProp ?? ctx.additionalInfo?.[findingKey];

  if (!block || typeof block !== 'object') {
    return null;
  }

  const title = titleFor(t, findingKey, labelKey);

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title={t('explain.seeExplainability', { defaultValue: 'See explainability' })}
        aria-label={`${t('explain.seeExplainability', { defaultValue: 'See explainability' })} — ${title}`}
        className="shrink-0 p-0.5 rounded text-slate-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors align-middle"
      >
        <LuEye size={size} />
      </button>
      {open && (
        <FindingExplainabilityModal
          findingKey={findingKey}
          block={block}
          studyUID={studyUIDProp}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export default FindingExplainabilityModal;
